/**
 * todoDue — when is a to-do due, in the words a person uses.
 *
 * One answer for every to-do surface (the /todos table's Due cell + "Due"
 * grouping today; the Focus lens in the to-dos upgrade brief,
 * docs/design-audit/TODOS-UPGRADE-SEND-TO-DESIGN.md, next). Buckets are
 * CALENDAR days in the viewer's zone, not 24-hour windows: something due
 * yesterday at 5pm is overdue this morning, not "due today".
 */

export type TodoDueBucket = 'overdue' | 'none' | 'today' | 'tomorrow' | 'this_week' | 'later';

/** Top-to-bottom order of the buckets. "Needs a date" sits right under
 *  Overdue on purpose — a to-do with no date is the one that gets forgotten,
 *  so it is never allowed to sink to the bottom of the list. */
export const TODO_DUE_BUCKET_ORDER: readonly TodoDueBucket[] = [
  'overdue', 'none', 'today', 'tomorrow', 'this_week', 'later',
];

export const TODO_DUE_BUCKET_LABEL: Record<TodoDueBucket, string> = {
  overdue: 'Overdue',
  none: 'Needs a date',
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  later: 'Later',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

/** Local midnight of the day a stored due value falls on. A bare
 *  `YYYY-MM-DD` is a DAY (read as local midnight, never UTC — the
 *  evening-before trap); anything else is an instant. */
function localDay(raw: string): Date | null {
  const d = new Date(DATE_ONLY.test(raw) ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function todoDueBucket(raw: string | null | undefined, now: Date = new Date()): TodoDueBucket {
  if (!raw) return 'none';
  const day = localDay(String(raw));
  if (!day) return 'none';
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Round, not floor: a DST day is 23 or 25 hours long.
  const diff = Math.round((day.getTime() - today.getTime()) / DAY_MS);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  // The to-dos design (2026-10-04): "This week" runs to seven days out, so a
  // to-do on its one-week suggested date lands in This week, not Later.
  if (diff <= 7) return 'this_week';
  return 'later';
}

/** Whole calendar days from today to the due day (negative = late), or null. */
export function todoDueDays(raw: string | null | undefined, now: Date = new Date()): number | null {
  if (!raw) return null;
  const day = localDay(String(raw));
  if (!day) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((day.getTime() - today.getTime()) / DAY_MS);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Oct 8" — a due day as the chips print it. */
export function todoDueShort(raw: string | null | undefined): string {
  if (!raw) return 'No date';
  const day = localDay(String(raw));
  return day ? `${MONTHS[day.getMonth()]} ${day.getDate()}` : 'No date';
}

/**
 * The due chip's words, in the design's grammar: `Today`, `Tomorrow`,
 * `Thu · Oct 8` within the week, `Sep 30 · 3d late` when late, `Oct 20` beyond,
 * `Needs a date` when there is none, and `Oct 10 · suggested` while the date is
 * still the app's one-week suggestion rather than a date a person chose.
 */
export function todoDueLabel(raw: string | null | undefined, suggested = false, now: Date = new Date()): string {
  const n = todoDueDays(raw, now);
  if (n === null) return 'Needs a date';
  if (suggested) return `${todoDueShort(raw)} · suggested`;
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n < 0) return `${todoDueShort(raw)} · ${-n}d late`;
  if (n <= 7) {
    const day = localDay(String(raw))!;
    return `${DOW[day.getDay()]} · ${todoDueShort(raw)}`;
  }
  return todoDueShort(raw);
}

/** The due date a new to-do starts with: one week out, as a local
 *  `YYYY-MM-DD` day. Michael, 2026-10-03: "a suggested default due date a
 *  week out just to ensure if someone forgets to add a due date there's one
 *  there." It is visible and editable in the create form. */
export function defaultTodoDueDate(now: Date = new Date(), days = 7): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
